import dayjs from "dayjs";
import { getInvoiceByStatus, getLocalTimeDate, getOrderDataForDayend, getUserStatusData, getUTCDate, updateLastdayEnd } from "../../services/manageOrderService";
import { getConfigData } from "../../services/configurationService";
import store from "../../stateManagement/store";
import { RESET_DAYEND_POPUP, RESET_DAYEND_STATE, SET_DAYEND_STATE } from "../../stateManagement/types/dayEndTypes";
import { getDataFromLocalDb, getModel, insertDataIntoLocalDB } from "../../services/dbService";
import utc from "dayjs/plugin/utc";
import { StatusType } from "./DayEndBox";
dayjs.extend(utc)

export const checkDayEndStatus = (): boolean => {
  let dayEndStatus = false;

  const dayEndObj = getDayEndStatusFromSession();

  if (dayEndObj && dayEndObj.isDayEndInProgress) {
    dayEndStatus = true;
  }

  return dayEndStatus;
}

export const isFeatureValid = (id: string) => {
  const dayEndObj = getDayEndStatusFromSession()
  if (dayEndObj && dayEndObj.isDayEndInProgress){
    return dayEndObj.mandatoryFeature.includes(id.trim());
  }
  return true
}
export const isSideBarOptionValid = (id: string) => {
  const dayEndObj = getDayEndStatusFromSession()
  if (dayEndObj && dayEndObj.isDayEndInProgress){
    return dayEndObj.mandatorySidebarFeature.includes(id.trim());
  }
  return true
}

const getOutForDeliveryInvoices = (data: any[]) => {
  return data
    .filter(d => d.status?.toUpperCase() === "OUT FOR DELIVERY")
    .map(d => d.invoiceNumber);
};

const getOrderCount = (data: any[]) => {
  let pendingCount = 0;
  let invoicedCount = 0;
  let outForDeliveryCount = 0;

  data.forEach((order: any) => {
    if (order.status.toUpperCase() === 'PENDING') {
      pendingCount += 1;
    } else if (order.status.toUpperCase() === 'INVOICED' || order.status.toUpperCase() === 'PARTIALLY INVOICED') {
    invoicedCount += 1;
    } else if (order.status.toUpperCase() === 'OUT FOR DELIVERY') {
      outForDeliveryCount += 1;
    }
  });

  return { pendingCount, invoicedCount, outForDeliveryCount };
}


const getStatusFromActivity = (activity: string[]) => {
  const status: string[] = []

  activity.forEach((a) => {
    if (a === "Invoicing"){
      status.push("PENDING")
    } else if (a === "Loadout"){
      status.push("INVOICED")
      status.push("PARTIALLY INVOICED")
    } else if (a === "Settlement"){
      status.push("OUT FOR DELIVERY")
    }
  })
  return status
}

//Store the status in DB. on reload and redirect this method gets called. If status exists in DB, then use that
//to populate the data, and not call an API.
export const setDayEndStatusInSession = async () => {
  //Instead of storing in session, store in a redux state and use it. On reload populate that state, since the redux state is in memory user cannot edit it easily
  const dayEndConfig = await getConfigData("dms_configuration", "dayEndConfig");
  const dayEndEnabled = dayEndConfig[0]?.configValue ?? false

  const companyPortalLoginIdConfig = await getConfigData("dms_configuration", "companyPortalLoginId");
  const designation = JSON.parse(localStorage.authContext).user.designation || []; 
  const isCompanyPortal = designation.includes(companyPortalLoginIdConfig[0]?.configValue ?? "reportadmin")

  const channelDivision = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user?.channelDivision;

  //if config disabled or if user not distributor return
  if (!dayEndEnabled || !channelDivision || isCompanyPortal) return;
  const userStatusData = await getUserStatusData();

  const defaultStartDiffConfig = await getConfigData("dms_configuration", "defaultStartDiffForDayEnd");
  const defaultStartDate = defaultStartDiffConfig[0]?.configValue ?? dayjs().subtract(5)

  const defaultstart = dayjs(defaultStartDate, "DD-MM-YYYY")

  const numberOfDaysConfig = await getConfigData(
    "dms_configuration",
    "numberOfDaysForDayEnd"
  );

  const numberOfDays = numberOfDaysConfig[0]?.configValue ?? 2;//should be 2
  const userLevelDayEndCheck =
    userStatusData?.extendedAttributes?.noOfDays ?? numberOfDays;

  const start = userStatusData?.lastAppUsed
    ? dayjs(userStatusData?.lastAppUsed)
    : defaultstart;
  const startDate = getLocalTimeDate(start, "start"); // returns string

  const d = userStatusData?.currentDateAndTime
    ? dayjs(getLocalTimeDate(dayjs(userStatusData?.currentDateAndTime), "end"))
    : dayjs(getLocalTimeDate(dayjs(), "end"));

  const end = d.subtract(userLevelDayEndCheck * 24, "hours");
  const endDate = getLocalTimeDate(end, "end");

  if (dayjs(startDate).isAfter(dayjs(endDate)) || !dayEndEnabled) {
    return;
  }

  const mandatoryActivityForDayEndConfig = await getConfigData("dms_configuration", "mandatoryStatusForDayEnd");
  const mandatoryActivityForDayEnd = mandatoryActivityForDayEndConfig[0]?.configValue ?? ["Invoicing", "Loadout", "Settlement"]
  const mandatoryActivity =
    userStatusData?.extendedAttributes?.requiredAction
      ? userStatusData.extendedAttributes.requiredAction.split(",")
      : mandatoryActivityForDayEnd;
  const mandatoryStatus = getStatusFromActivity(mandatoryActivity)

  const {mandatoryFeature, mandatorySidebarFeature} = getMandatoryFeatureConfig(mandatoryStatus)

  //If there are any pending/invoiced/outfordelivery orders between startDate and endDate, set day end in progress
  let data;
  let pendingCount = 0;
  let invoicedCount = 0;
  let outForDeliveryCount = 0;
  let outForDeliveryInvoices: string[] = [];
  let invoices: string[] = [];
  const cacheData = await getDayEndFromDB()
  if (!cacheData){
    data = await getOrderDataForDayend(startDate, endDate, mandatoryStatus);
    const cnt = getOrderCount(data)
    outForDeliveryInvoices = getOutForDeliveryInvoices(data);
    invoices = getInvoices(data);
    pendingCount = cnt.pendingCount
    invoicedCount = cnt.invoicedCount
    outForDeliveryCount = cnt.outForDeliveryCount
  }
  else{
    data = cacheData;
    pendingCount = data.pendingCount
    invoicedCount = data.invoicedCount
    outForDeliveryCount = data.outForDeliveryCount
    outForDeliveryInvoices = data.outForDeliveryInvoices
    invoices = data.invoices
  }

  if (pendingCount > 0 || invoicedCount > 0 || outForDeliveryCount > 0 || data.isDayEndInProgress) {
    const obj = {
      isDayEndInProgress: true,
      pendingCount,
      invoicedCount,
      outForDeliveryCount,
      startDate,
      endDate,
      status: {
        invoice: (pendingCount === 0) ? 'completed' :'notStarted',
        loadout: (invoicedCount === 0) ? 'completed' : 'notStarted',
        delivery: (outForDeliveryCount === 0) ? 'completed' : 'notStarted',
      },
      outForDeliveryInvoices,
      invoices,
      mandatoryStatus,
      mandatoryFeature,
      mandatorySidebarFeature,
      currentDateAndTime: d,
    };
    store.dispatch({
      type: SET_DAYEND_STATE,
      payload: { dayEndState: obj },
    });
    await setDayEndInDB(obj);
  }
}
export const getDayEndStatusFromSession = () => {
  const d = store.getState().dayEnd.dayEndState
  if (d) return d.dayEndState
  return null;
}

const getInvoices = (data: any[]) => {
  return data
    .filter(d => d.status?.toUpperCase() === "INVOICED")
    .map(d => d.orderNumber);
};

export const updateDayEndStatusInSession = async () => {
  const dayEndConfig = await getConfigData("dms_configuration", "dayEndConfig");
  const dayEndEnabled = dayEndConfig[0]?.configValue ?? false
  const channelDivision = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user?.channelDivision;

  if (!dayEndEnabled || !channelDivision) return;


  const obj = getDayEndStatusFromSession();

  const data = await getOrderDataForDayend(obj.startDate, obj.endDate, obj.mandatoryStatus);

  const { pendingCount, invoicedCount, outForDeliveryCount } = getOrderCount(data);
  obj.pendingCount = pendingCount;
  obj.invoicedCount = invoicedCount;
  obj.outForDeliveryCount = outForDeliveryCount;
  obj.status = {
    invoice: (pendingCount <= 0) ? StatusType.Completed : (obj.status.invoice === StatusType.NotStarted ? StatusType.NotStarted : StatusType.InProgress),
    loadout: (invoicedCount <= 0) ? StatusType.Completed : (obj.status.loadout === StatusType.NotStarted ? StatusType.NotStarted : StatusType.InProgress),
    delivery: (outForDeliveryCount <= 0) ? StatusType.Completed : (obj.status.delivery === StatusType.NotStarted ? StatusType.NotStarted : StatusType.InProgress),
  }
  obj.invoices = 
  obj.outForDeliveryInvoices = getOutForDeliveryInvoices(data);
  obj.invoices = getInvoices(data);
  if (pendingCount === 0 && invoicedCount === 0 && outForDeliveryCount === 0) {
    obj.canMarkDayEnd = true;
  }
  store.dispatch({
    type: SET_DAYEND_STATE,
    payload: {
      dayEndState: obj
    }
  })
  await setDayEndInDB(obj)
}

export const updateActivityStatus = (activity: string, status: string) => {
  const obj = getDayEndStatusFromSession();
  if (obj) {
    obj.status[activity] = status;
    store.dispatch({
      type: SET_DAYEND_STATE,
      payload: {
        dayEndState: obj
      }
    })
    setDayEndInDB(obj)
  }
}

export const completeDayEnd = async () => {
  store.dispatch({
    type: RESET_DAYEND_POPUP
  })
  await clearDayEndFromDB();
}

export const getDayEndFromDB: () => Promise<any | null> = async () => {
  const data = (await getDataFromLocalDb("application_data", { property: "dayEnd" })).data
  return data.length ? data[0].value : null;
}

export const setDayEndInDB = async (data: any) => {
  await insertDataIntoLocalDB("application_data", [{ value: data, property: "dayEnd"}])
}

export const clearDayEndFromDB = async (featureName ?: string) => {
  const model = await getModel("application_data");
  await model.delete({ property: "dayEnd" })
}

export const getOutForDeliveryInvoiceNumber = async () => {
  const dayEndObj = getDayEndStatusFromSession();
  const data: any[] = await getInvoiceByStatus(dayEndObj.startDate, dayEndObj.endDate, "OUT FOR DELIVERY") 
  return data.map((d: any) => d.invoiceNumber);
}

export const updateLasteEndDate = async () => {
  const obj = getDayEndStatusFromSession();
  const dayEndObjEndDate = obj.endDate
  const userStatusData = await getUserStatusData();
  const date1 = dayjs(getUTCDate(dayjs(userStatusData.lastAppUsed), "end"))
  const date2 = dayjs(getUTCDate(dayjs(dayEndObjEndDate), "end"))
  if(date1.isSame(date2)) throw new Error("alreadyMarked");
  const loginId = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user?.loginId;
  const requestBody = {
    loginId: loginId,
    lastAppUsed: dayEndObjEndDate
  }
  const res = await updateLastdayEnd(requestBody)
}

function getMandatoryFeatureConfig(mandatoryStatus: string[]) {
  const mandatoryFeature: string[] = ["Home", "MDM and Integration", "MDM and Reports"];
  const mandatorySidebarFeature: string[] = ["homePage", "reports", "mdm2"];

  // Case 1: PENDING
  if (mandatoryStatus.includes("PENDING")) {
    mandatoryFeature.push("Secondary");
    mandatorySidebarFeature.push("manageOrder", "manageInvoice");
  }

  // Case 2: INVOICED or PARTIALLY INVOICED
  if (
    mandatoryStatus.includes("INVOICED") ||
    mandatoryStatus.includes("PARTIALLY INVOICED")
  ) {
    if (!mandatoryStatus.includes("PENDING")){
      mandatoryFeature.push("Secondary");
      mandatorySidebarFeature.push("manageOrder", "manageInvoice");
    }
    mandatoryFeature.push("Delivery");
    mandatorySidebarFeature.push("deliverySettlement");
  }

  // Case 3: OUT FOR DELIVERY
  if (mandatoryStatus.includes("OUT FOR DELIVERY")) {
    mandatoryFeature.push("Settlements");
    mandatorySidebarFeature.push("loadoutDeliveryConfirmation");
  }

  return {
    mandatoryFeature,
    mandatorySidebarFeature,
  };
}

export function checkUserInvoicingPermission(){
  const dayEndObj = getDayEndStatusFromSession();
  //if dayend is in progress and user can perform action on pending order
  if (dayEndObj && dayEndObj.isDayEndInProgress && dayEndObj.mandatoryStatus.includes("PENDING")){
    return true;
  //if dayend is in progress and user cannot perform action on pending order
  } else if (dayEndObj && dayEndObj.isDayEndInProgress && !dayEndObj.mandatoryStatus.includes("PENDING")){
    return false;
  } else {
    return true;
  }
}
